import { Actions, createEffect, ofType } from '@ngrx/effects';
import { inject, Injectable } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { PersistenceService } from '@services/persistence.service';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { UserConfigActions } from '@store/actions/user-config.actions';
import { selectUserConfig } from '@store/selectors/user-config.selectors';
import { UserConfig, defaultUserConfig } from '@models/user-config.model';

@Injectable()
export class UserConfigEffects {
  //I was trying to use action$ in constructor before, but it was giving me error as undefined pipe.
  //the property loadUserConfig$ is called first before constructor, so I moved actions$ out of constructor.
  private actions$ = inject(Actions);
  private store = inject(Store<AppState>);
  constructor(private persistenceService: PersistenceService) { }

  resetView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserConfigActions.resetView),
      tap(() => {
        this.persistenceService.saveUserConfigState(defaultUserConfig);
      }), 
      map(() => UserConfigActions.loadUserConfigSuccess({ userConfig: defaultUserConfig }))
    )
  );

  loadUserConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserConfigActions.loadUserConfig),
      mergeMap(() =>
        this.persistenceService.loadState().then(
          (userConfig: UserConfig | undefined) => {
            const merged: UserConfig = {
              ...defaultUserConfig,
              ...(userConfig ?? {}),
            };
            return UserConfigActions.loadUserConfigSuccess({ userConfig: merged });
          }
        )
      ),
      catchError((error) => {
        console.error('Error loading user config:', error);
        return of(
          UserConfigActions.loadUserConfigSuccess({
            userConfig: defaultUserConfig,
          })
        );
      })
    )
  );

  saveUserConfig$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          UserConfigActions.updateLeftSideContainerWidth,
          UserConfigActions.updateRightSideContainerWidth,
          UserConfigActions.updateFramesHeight
        ),
        withLatestFrom(this.store.select(selectUserConfig)), // Use withLatestFrom to get state
        tap(([action, userConfig]) => {
          this.persistenceService.saveUserConfigState(userConfig);
        }),
        map(() => UserConfigActions.loadUserConfig())
      ),
    { dispatch: false }
  );

  updateLeftSideContainerWidth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserConfigActions.updateLeftSideContainerWidth),
        // Use withLatestFrom to avoid subscribing inside tap
        withLatestFrom(this.store.select(selectUserConfig)),
        tap(([action, userConfig]) => {
          const updatedConfig = { ...userConfig, leftSideContainerWidth: action.width };
          this.persistenceService.saveUserConfigState(updatedConfig);
        })
      ),
    { dispatch: false }
  );

  updateRightSideContainerWidth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserConfigActions.updateRightSideContainerWidth),
        // Use withLatestFrom to avoid subscribing inside tap
        withLatestFrom(this.store.select(selectUserConfig)),
        tap(([action, userConfig]) => {
          const updatedConfig = { ...userConfig, rightSideContainerWidth: action.width };
          this.persistenceService.saveUserConfigState(updatedConfig);
        })
      ),
    { dispatch: false }
  );

  persistCanvasHintsExpanded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserConfigActions.setCanvasHintsExpanded),
        withLatestFrom(this.store.select(selectUserConfig)),
        tap(([, userConfig]) => {
          this.persistenceService.saveUserConfigState(userConfig);
        })
      ),
    { dispatch: false }
  );
}